import { useState } from 'react';
import { api } from '../lib/api.js';
import { todayStr } from '../lib/format.js';

// ─── Add Team Member Modal (R45) ────────────────────────────────────────────
// Owner-only direct add — no Square dependency. Generates a temp 4-digit
// PIN + temp password and shows them so the owner can hand them to the
// staffer. The temp password forces a change on first email login.
export function AddTeamMemberModal({ currentUser, locations, onClose, onCreated }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [form, setForm] = useState({
    name: '', email: '', phone: '',
    role: 'Barista', staff_position: 'Barista', pay_entity: 'ETC',
    location_ids: [], primary_location_id: '',
    hourly_rate: '', hire_date: todayStr(),
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [created, setCreated] = useState(null);

  function toggleLoc(id) {
    setForm(f => ({
      ...f,
      location_ids: f.location_ids.includes(id) ? f.location_ids.filter(x => x !== id) : [...f.location_ids, id],
      primary_location_id: f.primary_location_id || id,
    }));
  }
  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim()) { setErr('Name is required.'); return; }
    if (form.location_ids.length === 0) { setErr('Pick at least one location.'); return; }
    setBusy(true); setErr('');
    try {
      const r = await api('POST', '/api/admin/users', {
        ...form,
        hourly_rate: form.hourly_rate === '' ? null : Number(form.hourly_rate),
      }, headers);
      setCreated(r);
    } catch (e) { setErr(e.message || 'Save failed'); }
    finally { setBusy(false); }
  }

  if (created) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', zIndex: 60, padding: 16 }}>
        <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 480, padding: 20 }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>✓ {form.name} added</div>
          <div style={{ fontSize: 13, color: '#374151', marginBottom: 12 }}>
            Hand these credentials to the new staffer. They'll be prompted to set a real password on first email login.
          </div>
          <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: '#6b7280' }}>Kiosk PIN</div>
            <div style={{ fontSize: 22, fontWeight: 800, fontFamily: 'monospace', color: '#212121', marginBottom: 8 }}>{created.temp_pin}</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>Temp password</div>
            <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'monospace', color: '#212121' }}>{created.temp_password}</div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button onClick={() => { setCreated(null); onCreated && onCreated(); }} className="btn-primary">Done</button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
                    alignItems: 'flex-start', justifyContent: 'center', zIndex: 60, padding: 16, overflowY: 'auto' }}>
      <form onSubmit={submit}
        style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 520, display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: 16, borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>Add team member</div>
            <div style={{ fontSize: 11, color: '#9e9e9e' }}>Owner only.</div>
          </div>
          <button type="button" onClick={onClose}
            style={{ background: 'transparent', border: 'none', fontSize: 22, color: '#9e9e9e', cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <label style={{ fontSize: 12, fontWeight: 600 }}>
            Name *
            <input type="text" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} required
              style={{ display: 'block', width: '100%', padding: 8, border: '1px solid #d0d4d9', borderRadius: 6, marginTop: 4 }} />
          </label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <label style={{ fontSize: 12, fontWeight: 600, flex: 1, minWidth: 200 }}>
              Email
              <input type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                style={{ display: 'block', width: '100%', padding: 8, border: '1px solid #d0d4d9', borderRadius: 6, marginTop: 4 }} />
            </label>
            <label style={{ fontSize: 12, fontWeight: 600, flex: 1, minWidth: 160 }}>
              Phone
              <input type="tel" value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
                placeholder="(555) 123-4567"
                style={{ display: 'block', width: '100%', padding: 8, border: '1px solid #d0d4d9', borderRadius: 6, marginTop: 4 }} />
            </label>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <label style={{ fontSize: 12, fontWeight: 600, flex: 1, minWidth: 140 }}>
              Role
              <select value={form.role} onChange={(e) => setForm(f => ({ ...f, role: e.target.value }))}
                style={{ display: 'block', width: '100%', padding: 8, border: '1px solid #d0d4d9', borderRadius: 6, marginTop: 4 }}>
                {['Barista', 'Shift Lead', 'Kitchen', 'Opening Staff', 'Closing Staff', 'Manager', 'Owner'].map(r => <option key={r}>{r}</option>)}
              </select>
            </label>
            <label style={{ fontSize: 12, fontWeight: 600, flex: 1, minWidth: 140 }}>
              Position
              <select value={form.staff_position} onChange={(e) => setForm(f => ({ ...f, staff_position: e.target.value }))}
                style={{ display: 'block', width: '100%', padding: 8, border: '1px solid #d0d4d9', borderRadius: 6, marginTop: 4 }}>
                {['Barista', 'Employee', 'Manager', 'Roaster'].map(p => <option key={p}>{p}</option>)}
              </select>
            </label>
            <label style={{ fontSize: 12, fontWeight: 600, flex: 1, minWidth: 100 }}>
              Pay entity
              <select value={form.pay_entity} onChange={(e) => setForm(f => ({ ...f, pay_entity: e.target.value }))}
                style={{ display: 'block', width: '100%', padding: 8, border: '1px solid #d0d4d9', borderRadius: 6, marginTop: 4 }}>
                {['ETC', 'NBT'].map(p => <option key={p}>{p}</option>)}
              </select>
            </label>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Locations</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(locations || []).map(l => (
                <label key={l.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px',
                                              border: `1px solid ${form.location_ids.includes(l.id) ? 'var(--forest, #1B2B1B)' : '#d0d4d9'}`,
                                              borderRadius: 6, fontSize: 12, cursor: 'pointer',
                                              background: form.location_ids.includes(l.id) ? '#eef2f6' : '#fff' }}>
                  <input type="checkbox" checked={form.location_ids.includes(l.id)}
                    onChange={() => toggleLoc(l.id)} />
                  {l.name}
                </label>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <label style={{ fontSize: 12, fontWeight: 600, flex: 1, minWidth: 140 }}>
              Hourly rate ($)
              <input type="number" step="0.01" min="0" value={form.hourly_rate}
                onChange={(e) => setForm(f => ({ ...f, hourly_rate: e.target.value }))}
                placeholder="e.g. 16.50"
                style={{ display: 'block', width: '100%', padding: 8, border: '1px solid #d0d4d9', borderRadius: 6, marginTop: 4 }} />
            </label>
            <label style={{ fontSize: 12, fontWeight: 600, flex: 1, minWidth: 140 }}>
              Hire date
              <input type="date" value={form.hire_date}
                onChange={(e) => setForm(f => ({ ...f, hire_date: e.target.value }))}
                style={{ display: 'block', width: '100%', padding: 8, border: '1px solid #d0d4d9', borderRadius: 6, marginTop: 4 }} />
            </label>
          </div>
          {err && <div style={{ fontSize: 12, color: '#c62828' }}>{err}</div>}
          <div style={{ fontSize: 11, color: '#9e9e9e' }}>
            A 4-digit kiosk PIN and a temp password will be generated. You'll see both on the next screen.
          </div>
        </div>
        <div style={{ padding: 12, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="submit" disabled={busy} className="btn-primary">
            {busy ? 'Adding…' : 'Add team member'}
          </button>
        </div>
      </form>
    </div>
  );
}
